import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ApiClientService } from '../../core/api/api-client.service';
import { AuthService } from '../../core/auth/auth.service';
import { CompanySummary, CreateCompanyRequest, OrganizationSummary, PageResponse } from '../../core/models/portal.models';

const EMPTY_COMPANY_FORM: CreateCompanyRequest = {
  organizationId: null,
  name: '',
  street: '',
  postalCode: '',
  city: '',
  country: 'Deutschland',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  concessionNumber: '',
  shortCode: '',
  vehicleCount: null,
  paymentStatus: 'Testphase',
  paymentValidUntil: null,
  packageType: 'Nicht festgelegt',
  enabledModules: '',
  internalPaymentNote: '',
  status: 'ACTIVE'
};

@Component({
  selector: 'app-companies-page',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './companies-page.component.html'
})
export class CompaniesPageComponent implements OnInit {
  private readonly api = inject(ApiClientService);
  private readonly auth = inject(AuthService);
  protected readonly companies = signal<PageResponse<CompanySummary> | null>(null);
  protected readonly search = signal('');
  protected readonly status = signal('');
  protected readonly createDialogOpen = signal(false);
  protected readonly form = signal<CreateCompanyRequest>({ ...EMPTY_COMPANY_FORM });
  protected readonly createError = signal<string | null>(null);
  protected readonly deleteError = signal<string | null>(null);
  protected readonly saving = signal(false);
  protected readonly canDelete = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN', 'ORGANIZATION_ADMIN']));
  protected readonly canSelectOrganization = computed(() => this.auth.hasAnyRole(['SUPER_ADMIN']));
  protected readonly organizationOptions = signal<OrganizationSummary[]>([]);

  ngOnInit(): void {
    this.load();
    if (this.canSelectOrganization()) {
      this.api.organizations(0, 100).subscribe((page) => this.organizationOptions.set(page.items));
    }
  }

  protected load(): void {
    this.api.companies(0, 50, this.search(), this.status()).subscribe((companies) => this.companies.set(companies));
  }

  protected reset(): void {
    this.search.set('');
    this.status.set('');
    this.load();
  }

  protected openCreateDialog(): void {
    this.createError.set(null);
    this.form.set({ ...EMPTY_COMPANY_FORM });
    this.createDialogOpen.set(true);
  }

  protected closeCreateDialog(): void {
    if (this.saving()) {
      return;
    }
    this.createDialogOpen.set(false);
    this.createError.set(null);
  }

  protected createCompany(event: Event): void {
    event.preventDefault();
    const request = this.normalizedCreateRequest();

    if (!request.name) {
      this.createError.set('Bitte gib einen Unternehmensnamen ein.');
      return;
    }

    if (this.canSelectOrganization() && !request.organizationId) {
      this.createError.set('Bitte wähle eine Organisation aus.');
      return;
    }

    this.saving.set(true);
    this.createError.set(null);

    this.api.createCompany(request).subscribe({
      next: () => {
        this.saving.set(false);
        this.createDialogOpen.set(false);
        this.load();
      },
      error: () => {
        this.saving.set(false);
        this.createError.set('Der Unternehmer konnte nicht angelegt werden.');
      }
    });
  }

  protected deleteCompany(company: CompanySummary): void {
    this.deleteError.set(null);

    if (!window.confirm(`Unternehmer "${company.name}" wirklich löschen? Zugehörige Fahrer, Benutzer und Lernfortschritte werden entfernt.`)) {
      return;
    }

    this.api.deleteCompany(company.id).subscribe({
      next: () => this.load(),
      error: () => this.deleteError.set('Der Unternehmer konnte nicht gelöscht werden.')
    });
  }

  protected updateForm(field: keyof CreateCompanyRequest, event: Event): void {
    const value = (event.target as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement).value;
    this.form.update((current) => ({ ...current, [field]: value }));
  }

  protected updateNumberForm(field: keyof CreateCompanyRequest, event: Event): void {
    const raw = (event.target as HTMLInputElement).value;
    this.form.update((current) => ({ ...current, [field]: raw === '' ? null : Number(raw) }));
  }

  protected setSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value);
  }

  protected setStatus(event: Event): void {
    this.status.set((event.target as HTMLSelectElement).value);
  }

  protected setOrganization(event: Event): void {
    const value = (event.target as HTMLSelectElement).value;
    this.form.update((current) => ({ ...current, organizationId: value ? Number(value) : null }));
  }

  protected valueOrDash(value: string | null): string {
    return value?.trim() ? value : '-';
  }

  protected formatScore(value: number | null): string {
    return value === null ? '-' : `${value} %`;
  }

  private normalizedCreateRequest(): CreateCompanyRequest {
    const current = this.form();
    return {
      organizationId: current.organizationId ?? null,
      name: current.name.trim(),
      street: this.clean(current.street),
      postalCode: this.clean(current.postalCode),
      city: this.clean(current.city),
      country: this.clean(current.country) ?? 'Deutschland',
      contactName: this.clean(current.contactName),
      contactEmail: this.clean(current.contactEmail),
      contactPhone: this.clean(current.contactPhone),
      concessionNumber: this.clean(current.concessionNumber),
      shortCode: this.clean(current.shortCode),
      vehicleCount: current.vehicleCount ?? null,
      paymentStatus: this.clean(current.paymentStatus),
      paymentValidUntil: this.clean(current.paymentValidUntil),
      packageType: this.clean(current.packageType),
      enabledModules: this.clean(current.enabledModules),
      internalPaymentNote: this.clean(current.internalPaymentNote),
      status: current.status
    };
  }

  private clean(value: string | null | undefined): string | null {
    const trimmed = value?.trim();
    return trimmed ? trimmed : null;
  }
}
